import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, ShieldOff, ShieldCheck, Loader2, Lock, FileArchive } from 'lucide-react';

// Admin › Green IA › Cession / vente (super admin : PIN + date de naissance)
export const GreenIADivestPanel = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ buyer_name: '', buyer_contact: '', amount: '', currency: 'EUR', note: '', pin: '', birth_date: '' });
  const [busy, setBusy] = useState('');

  const load = () => axios.get('/api/admin/green-ia/divestiture').then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const creds = () => ({ pin: form.pin.trim(), birth_date: form.birth_date.trim() });
  const credsOk = form.pin.trim().length >= 4 && form.birth_date.trim().length >= 8;

  const downloadKit = async () => {
    if (!credsOk) { toast.error('PIN et date de naissance requis'); return; }
    setBusy('kit');
    try {
      const r = await axios.get('/api/admin/green-ia/sale-kit', { params: creds(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `green-ia-kit-${Date.now()}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Kit de cession téléchargé');
    } catch (e) {
      let msg = 'Téléchargement impossible';
      try { msg = JSON.parse(await e?.response?.data?.text())?.detail || msg; } catch (_) {}
      toast.error(msg);
    } finally { setBusy(''); }
  };

  const divest = async () => {
    if (!credsOk) { toast.error('PIN et date de naissance requis'); return; }
    if (!form.buyer_name.trim()) { toast.error("Nom de l'acquéreur requis"); return; }
    if (!window.confirm(`Retirer Green IA de l'app et enregistrer la cession à « ${form.buyer_name} » pour ${form.amount || 0} ${form.currency} ?`)) return;
    setBusy('divest');
    try {
      await axios.post('/api/admin/green-ia/divest', { ...form, ...creds(), amount: parseFloat(form.amount || '0') });
      toast.success('Green IA retirée de l\'app — cession enregistrée');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setBusy(''); }
  };

  const restore = async () => {
    if (!credsOk) { toast.error('PIN et date de naissance requis'); return; }
    if (!window.confirm('Réactiver Green IA dans l\'app ?')) return;
    setBusy('restore');
    try {
      await axios.post('/api/admin/green-ia/restore', creds());
      toast.success('Green IA réactivée');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setBusy(''); }
  };

  const st = data?.status;
  const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-green-400/60';

  return (
    <div className="mt-6 border-t border-white/10 pt-5" data-testid="green-ia-divest-panel">
      <h4 className="font-bold text-white flex items-center gap-2 mb-1"><FileArchive className="w-4 h-4 text-green-300" /> Cession / vente de Green IA</h4>
      <p className="text-xs text-gray-400 mb-4">Réservé au super admin. Permet de télécharger le kit à remettre à l'acquéreur (code + base de données + README), puis de retirer Green IA de l'app (onglet Déesse, page de bienvenue, Espace Pro). Réversible.</p>

      <div className={`rounded-xl p-3 mb-4 text-sm border ${st?.enabled ? 'bg-green-500/10 border-green-500/30 text-green-200' : 'bg-red-500/10 border-red-500/30 text-red-200'}`} data-testid="green-ia-divest-status">
        {st?.enabled ? 'Statut : Green IA active dans l\'app' : `Statut : Green IA RETIRÉE — cédée à ${st?.sold_to} pour ${st?.amount} ${st?.currency || ''} le ${st?.sold_at ? new Date(st.sold_at).toLocaleDateString('fr-FR') : ''}`}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input value={form.buyer_name} onChange={set('buyer_name')} placeholder="Nom de l'acquéreur *" className={inputCls} data-testid="divest-buyer" />
        <input value={form.buyer_contact} onChange={set('buyer_contact')} placeholder="Contact acquéreur (email / tél.)" className={inputCls} data-testid="divest-contact" />
        <input value={form.amount} onChange={set('amount')} type="number" min="0" step="0.01" placeholder="Montant *" className={inputCls} data-testid="divest-amount" />
        <select value={form.currency} onChange={set('currency')} className={inputCls} data-testid="divest-currency">
          {['EUR', 'USD', 'GBP', 'CHF', 'CAD', 'XOF', 'BRL'].map((c) => <option key={c} value={c} className="bg-gray-900">{c}</option>)}
        </select>
        <textarea value={form.note} onChange={set('note')} rows={2} placeholder="Note (but non lucratif, conditions, etc.)" className={`${inputCls} sm:col-span-2 resize-none`} data-testid="divest-note" />
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-4">
        <p className="text-xs text-gray-300 flex items-center gap-1.5 mb-2"><Lock className="w-3.5 h-3.5" /> Confirmation super admin</p>
        <div className="grid grid-cols-2 gap-3">
          <input value={form.pin} onChange={set('pin')} type="password" inputMode="numeric" placeholder="Code PIN super admin" className={inputCls} data-testid="divest-pin" />
          <input value={form.birth_date} onChange={set('birth_date')} placeholder="Date de naissance (JJ/MM/AAAA)" className={inputCls} data-testid="divest-birth" />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={downloadKit} disabled={!!busy} className="flex-1 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="divest-download-kit">
          {busy === 'kit' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Télécharger le kit de cession (ZIP)
        </button>
        {st?.enabled ? (
          <button onClick={divest} disabled={!!busy} className="flex-1 py-2.5 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="divest-remove">
            {busy === 'divest' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />} Retirer Green IA de l'app
          </button>
        ) : (
          <button onClick={restore} disabled={!!busy} className="flex-1 py-2.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="divest-restore">
            {busy === 'restore' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Restaurer Green IA
          </button>
        )}
      </div>

      <div className="mt-4 text-xs text-gray-400 space-y-1">
        <p className="font-semibold text-gray-300">Contenu du kit remis à l'acquéreur :</p>
        <p>• <b>code/backend</b> : ext_v509_routes.py (registre anti-deepfake, AP-IA, certificats), apia_routes.py (API Green APIA), cyberdefense_routes.py, green_ia_divest_routes.py</p>
        <p>• <b>code/frontend</b> : GreenIAPanel.jsx, GreenApiaProPanel.jsx, AdminV509Panels.jsx, CyberdefensePanel.jsx</p>
        <p>• <b>database/*.json</b> : green_ia_registry, green_apia_requests, green_apia_external_checks, green_ia_audit, apia_keys, apia_requests, apia_topups (import : mongoimport)</p>
        <p>• <b>README.md</b> : ce qu'il faut transmettre en plus (clés API séparément, marque, endpoints), procédure d'import et notes RGPD.</p>
      </div>

      {data?.history?.length > 0 && (
        <div className="mt-4" data-testid="divest-history">
          <p className="text-xs font-semibold text-gray-300 mb-1">Historique</p>
          {data.history.map((h) => (
            <p key={h.id} className="text-xs text-gray-400">{new Date(h.created_at).toLocaleString('fr-FR')} — {h.buyer_name} — {h.amount} {h.currency} — {h.status}{h.buyer_contact ? ` — ${h.buyer_contact}` : ''}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default GreenIADivestPanel;
